// ink-component
import * as React from 'react';
import styled, { css } from 'styled-components';

import { resetBrowserButton } from '../../../../../Button/styles';
import { newTableTokens } from '../../../../../../tokens/components/NewTable/tokens';
import { buttonTokens } from '../../../../../../tokens/components/Button/tokens';

import Icon from '../../../../../Icon/Icon';
import { useTwiddleExpandAllState } from '../../hooks';

export interface ExpandAllPropsType extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  totalLevels?: number;
}

/* @internal */
export const StyledExpandAll = styled.button<React.ButtonHTMLAttributes<HTMLButtonElement>>`
  ${resetBrowserButton};
  align-items: center;
  border-radius: ${newTableTokens.PinBorderRadiusBaseDefault};
  color: ${newTableTokens.TwiddleIconForegroundColorBaseDefault};
  cursor: pointer;
  display: flex;
  margin: 0 -6px;
  padding: 3px;

  ${({ theme }) =>
    theme.totalLevels &&
    css`
      margin-right: calc(${newTableTokens.CellSpacingHorizontalBaseDefault} * ${theme.totalLevels - 1});
    `};

  &:focus-visible {
    outline: ${buttonTokens.BorderWidthBaseDefault} ${buttonTokens.BorderStyleBaseDefault}
      ${buttonTokens.BorderColorBaseKeyboardFocus};
    box-shadow: 0 0 0 ${buttonTokens.ShadowWidthBaseKeyboardFocus} ${buttonTokens.ShadowColorBaseKeyboardFocus};
  }

  &:hover {
    background-color: ${newTableTokens.TwiddleIconBackgroundColorBaseHover};
  }
`;

/**
 * @purpose Allows users to Collapse or Expand all Rows inside a NewTable
 * @group NewTableTwiddle
 */
const TwiddleExpandAll = ({ style, className, totalLevels, onClick, ...otherProps }: ExpandAllPropsType) => {
  const { open, toggleAll } = useTwiddleExpandAllState();

  /*
    If all twiddles are open, the button should display the collapse icon
    If all twiddles are closed, the button should display the expand icon
    If some twiddles are open and some are closed, the button should display the collapse icon
    "Collapse rows" only shows up if ALL twiddles are open
  */

  return (
    <StyledExpandAll
      {...otherProps}
      theme={{ totalLevels }}
      type="button"
      onClick={e => {
        e.preventDefault();
        toggleAll();
        if (onClick) {
          onClick(e);
        }
      }}
    >
      <Icon size="lg" type={open ? 'collapse-all' : 'expand-all'} meaning={open ? 'Collapse rows' : 'Expand rows'} />
    </StyledExpandAll>
  );
};

TwiddleExpandAll.displayName = 'NewTable.Twiddle.ExpandAll';

export default TwiddleExpandAll;
